import { Injectable, inject } from '@angular/core';
import { ScoreService } from '../match-tracker/tracker-board/services/score.service';
import { PartnerConnectService } from '../_core/services/partner-connect.service';
import { ThemeService } from '../_core/services/theme.service';
import { Match, Schedule } from '../_shared/models/match.model';
import { Location } from '@angular/common';
import { HttpClient } from '@angular/common/http';

@Injectable({
    providedIn: 'root'
})
export class ScoreboardService {
    private location = inject(Location);
    private http = inject(HttpClient);

    constructor(
        private themeService: ThemeService,
        private scoreService: ScoreService,
        private pcs: PartnerConnectService
    ) { }

    getColor(sideNumber, tie): string {
        if (tie && tie?.sides[sideNumber - 1]?.team?.colors.length) {
            return tie.sides[sideNumber - 1].team.colors[0];
        } else {
            const isDarkMode = this.themeService.isDarkMode;
            return isDarkMode
                ? sideNumber === 1 ? '#03a9f4' : '#8bc34a'
                : sideNumber === 1 ? '#3f51b5' : '#009688';
        }
    }

    getTiebreakScore(match: Match, setIndex: number, sideNumber) {
        if (!this.scoreService.isMatchCompletedAndLastSetTiebreak(match, setIndex)) {
            return match.score?.sets?.length
                ? sideNumber === 1
                    ? match.score.sets[setIndex]?.side1TiebreakScore
                    : match.score.sets[setIndex]?.side2TiebreakScore
                : this.pcs.getTiebreakScore(match.sets[setIndex], sideNumber);
        } else {
            return null;
        }
    }

    getCourtName(match: Match): string {
        if (match?.collectionPosition && this.location.path().includes('stream-view')) {
            return `${match.collectionPosition}`
        }

        let courtName = '';
        if (match.schedule?.court?.courtName) {
            courtName = match.schedule.court?.courtName;
        } else if (match.schedule?.courtName) {
            courtName = match.schedule.courtName;
        } else {
            const index = match?.extensions?.findIndex(ext => ext.name === 'court');
            if (index > -1 && match.extensions[index].value.courtName?.length > 1) {
                courtName = match.extensions[index].value.courtName
            }
            
        }

        if (match.schedule?.venue?.venueAbbreviation) {
            courtName += ` at ${match.schedule.venue.venueAbbreviation}`
        } else if (match.schedule?.venueAbbreviation) {
            courtName += ` at ${match.schedule.venueAbbreviation}`
        }
                
        return courtName;
    }

    getTime(schedule: Schedule): string | undefined {
        const raw = schedule?.scheduledTime;
        const timeModifier = schedule?.timeModifiers?.[0] === 'NOT_BEFORE' ? 'NB' :
        schedule?.timeModifiers?.[0] === 'FOLLOWED_BY' ? 'Followed by' : 
        schedule?.timeModifiers?.[0] || '';
      
        const time = raw
          ?.replace(" ", "")
          ?.replace(/(\d+):(\d+)/, (_, h, m) => `${h % 12 || 12}:${m} ${h >= 12 ? "PM" : "AM"}`);
      
        return timeModifier && time
          ? `${timeModifier} ${time}`
          : timeModifier || time || schedule?.time;
    }

    getMatchTime(match: any): string {        
        
        // Check for schedule with time modifiers
        if (match?.schedule) {            
            const schedule = match.schedule;
            const timeModifiers = schedule?.timeModifiers || [];
            const timeStr = schedule?.scheduledTime ? this.formatTimeTo12Hour(schedule?.scheduledTime) : '';
            
            // If FOLLOWED_BY modifier exists, return "Followed By"
            if (timeModifiers.includes('FOLLOWED_BY')) {
                return `Followed By ${timeStr || ''}`;
            }
            
            // If NOT_BEFORE modifier exists, format the scheduled time
            if (timeModifiers.includes('NOT_BEFORE')) {        
                return `NB ${timeStr || ''}`;
            }
            
            // If scheduledTime exists, format it to 12-hour AM/PM
            if (schedule?.scheduledTime) {
                return this.formatTimeTo12Hour(schedule.scheduledTime);
            }
        }
        
        // Fallback to startDate if available
        if (match.startDate) {
            const d = new Date(match.startDate);
            return d.toLocaleTimeString([], { hour: '2-digit', minute: '2-digit', hour12: true });
        }
        
        return 'TBD';
      }
    
      private formatTimeTo12Hour(timeStr: string): string {
        // Handle formats like "15:15" or "15:15:00"
        const parts = timeStr.split(':');
        if (parts.length < 2) return timeStr;
        
        const hours = parseInt(parts[0], 10);
        const minutes = parts[1];
        
        if (isNaN(hours)) return timeStr;
        
        const period = hours >= 12 ? 'PM' : 'AM';
        const displayHours = hours === 0 ? 12 : hours > 12 ? hours - 12 : hours;
        
        return `${displayHours}:${minutes} ${period}`;
      }

    updatePlayerInformation(_matchId: string, side1Note: string, side2Note: string) {
        return this.http.post<any>('api_endpoint' + '/match/update-sides', { _matchId, side1Note, side2Note });
    }
}
